import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  BriefcaseBusiness,
  CheckCircle2,
  HeartHandshake,
  PlayCircle,
  Search,
  ShieldCheck,
  UserRound,
} from "lucide-react";
import YoutubeVideoLightbox from "../../components/YoutubeVideoLightbox";
import Old1PublicSiteNav from "../components/old1_PublicSiteNav";
import Old1PublicSiteFooter from "../components/old1_PublicSiteFooter";
import PublicInsightCard from "../../components/PublicInsightCard";
import PageSeo from "../../components/PageSeo";
import { DEFAULT_SITE_DESCRIPTION } from "../../lib/pageSeo";
import { JOBUWIN_INTRO_VIDEO_ID } from "../../lib/youtubeVideos";
import { PUBLIC_BLOG_POSTS } from "../../data/publicBlogPosts";

const HOW_IT_WORKS_YOUTUBE_ID = JOBUWIN_INTRO_VIDEO_ID;

const OLD1_BLOG_POSTS = PUBLIC_BLOG_POSTS.map((post) => ({
  ...post,
  href: post.href.replace(/^\/blog/, "/old1/blog"),
}));

function old1_HomePage({ currentUser, setCurrentUser }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [showHowItWorksVideo, setShowHowItWorksVideo] = useState(false);
  return (
    <div className="home-page jw-public-page jw-public-home">
      <PageSeo
        title="Genuine jobs. Real people."
        description={DEFAULT_SITE_DESCRIPTION}
        path="/old1/home"
        schemaType="website"
      />
      <Old1PublicSiteNav currentUser={currentUser} setCurrentUser={setCurrentUser} />

      <main id="main-content" className="jw-public-main">
        <section className="hero jw-public-hero">
          <div className="jw-public-hero__content">
            <span className="jw-public-chip">
              <ShieldCheck className="jw-public-icon" aria-hidden="true" />
              Verified hiring for real people
            </span>
            <h1 className="key-phrase jw-public-hero__title">
              <span className="key-phrase-primary-1">genuine jobs.</span>{" "}
              <span className="key-phrase-primary-2">real people.</span>
            </h1>
            <p className="jw-public-hero__copy">
              Designed to reduce fake profiles, AI generated noise, and meaningless applications through more
              authentic communication.
            </p>
            <div className="jw-public-action-row">
              <button
                className="jw-public-button jw-public-button--primary"
                type="button"
                onClick={() => navigate("/old1/signup?type=applicant")}
              >
                Find genuine jobs
                <Search className="jw-public-icon" aria-hidden="true" />
              </button>
              <button
                className="jw-public-button jw-public-button--secondary"
                type="button"
                onClick={() => navigate("/old1/signup?type=hiring-manager")}
              >
                Start hiring
                <BriefcaseBusiness className="jw-public-icon" aria-hidden="true" />
              </button>
              <button
                className="jw-public-button jw-public-button--outline"
                type="button"
                onClick={() => setShowHowItWorksVideo(true)}
              >
                <PlayCircle className="jw-public-icon" aria-hidden="true" />
                See our intro video
              </button>
            </div>
          </div>

          <div className="jw-public-hero__panel jw-public-why-panel" aria-label="Why JOBUWIN exists">
            <div className="jw-public-why-panel__head">
              <span className="jw-public-why-panel__icon" aria-hidden="true">
                <HeartHandshake className="jw-public-icon" />
              </span>
              <h2>Why JOBUWIN exists</h2>
            </div>
            <div className="jw-public-why-panel__body">
              <p>The job market is drowning in noise.</p>
              <p>
                Fake profiles. AI generated applications.
                <br />
                Misleading job ads.
              </p>
              <p>
                Applicants feel ignored.
                <br />
                Hiring managers feel overwhelmed.
              </p>
              <p>
                We built JOBUWIN to help
                <br />
                genuine people find each other again.
              </p>
            </div>
            <div className="jw-public-why-panel__links">
              <Link
                to="/old1/blog/hiring-is-broken"
                className="jw-public-why-link jw-public-why-link--p1"
              >
                <span className="jw-public-why-link__icon">
                  <BriefcaseBusiness className="jw-public-icon" aria-hidden="true" />
                </span>
                <span className="jw-public-why-link__text">
                  Learn more about hiring on JOBUWIN
                </span>
                <ArrowRight className="jw-public-why-link__arrow" aria-hidden="true" />
              </Link>
              <Link
                to="/old1/blog/applying-for-jobs-is-broken"
                className="jw-public-why-link jw-public-why-link--p2"
              >
                <span className="jw-public-why-link__icon">
                  <UserRound className="jw-public-icon" aria-hidden="true" />
                </span>
                <span className="jw-public-why-link__text">
                  Learn more about applying on JOBUWIN
                </span>
                <ArrowRight className="jw-public-why-link__arrow" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>

        <section className="jw-public-section">
          <div className="jw-public-section-head">
            <span className="jw-public-eyebrow">Choose your path</span>
            <h2>Start your JOBUWIN journey.</h2>
          </div>
          <div className="cards jw-public-path-grid">
            <article className="jw-public-card jw-public-path-card jw-public-path-card--applicant">
              <div className="jw-public-path-card__head">
                <div className="jw-public-card-icon jw-public-card-icon--p1">
                  <Search className="jw-public-icon-lg" aria-hidden="true" />
                </div>
                <h3>I am job searching</h3>
              </div>
              <p className="subtitle">Stand out through real experience, genuine intent, and authentic communication.</p>
              <div className="jw-public-path-card__actions">
                <button
                  className="jw-public-button jw-public-button--primary"
                  type="button"
                  onClick={() => navigate("/old1/signup?type=applicant")}
                >
                  Start applying
                  <ArrowRight className="jw-public-icon" aria-hidden="true" />
                </button>
                <p className="signin-link">
                  Have an account? <Link to="/old1/signin">Sign in</Link>
                </p>
              </div>
            </article>

            <article className="jw-public-card jw-public-path-card jw-public-path-card--hiring">
              <div className="jw-public-path-card__head">
                <div className="jw-public-card-icon jw-public-card-icon--p2">
                  <BriefcaseBusiness className="jw-public-icon-lg" aria-hidden="true" />
                </div>
                <h3>I am hiring</h3>
              </div>
              <p className="subtitle">Reduce noise, avoid spam, and connect with more genuine candidates.</p>
              <div className="jw-public-path-card__actions">
                <button
                  className="jw-public-button jw-public-button--secondary"
                  type="button"
                  onClick={() => navigate("/old1/signup?type=hiring-manager")}
                >
                  Start hiring
                  <ArrowRight className="jw-public-icon" aria-hidden="true" />
                </button>
                <p className="signin-link">
                  Have an account? <Link to="/old1/signin">Sign in</Link>
                </p>
              </div>
            </article>
          </div>
        </section>

        <section className="blog-section jw-public-section jw-public-insights">
          <div className="jw-public-section-head jw-public-insights__head">
            <span className="jw-public-eyebrow">Read our blog</span>
            <h2>Designed around trust, proof, and better decisions.</h2>
          </div>

          <div className="blog-grid jw-public-insight-grid">
            {OLD1_BLOG_POSTS.map((post) => (
              <PublicInsightCard key={post.href} {...post} />
            ))}
          </div>

          <div className="jw-public-insights__footer">
            <Link
              className="jw-public-button jw-public-button--outline jw-public-insights__see-all"
              to="/old1/blog"
              state={{ from: location.pathname }}
            >
              Explore Articles
              <ArrowRight className="jw-public-icon" aria-hidden="true" />
            </Link>
          </div>
        </section>

        <section className="jw-public-section jw-public-proof-panel">
          <div>
            <h2>A trusted place for genuine hiring.</h2>
            <p className="jw-public-proof-panel__copy">
              Designed to help hiring managers and applicants connect without fake jobs, fake applicants, spam, or
              ghost listings.
            </p>
          </div>
          <div className="jw-public-proof-list">
            <span><CheckCircle2 className="jw-public-icon" aria-hidden="true" /> Genuine jobs only</span>
            <span><CheckCircle2 className="jw-public-icon" aria-hidden="true" /> Responsible behaviour by design</span>
            <span><CheckCircle2 className="jw-public-icon" aria-hidden="true" /> Built around trust and authenticity</span>
          </div>
        </section>
      </main>

      <Old1PublicSiteFooter />

      <YoutubeVideoLightbox
        open={showHowItWorksVideo}
        onClose={() => setShowHowItWorksVideo(false)}
        videoId={HOW_IT_WORKS_YOUTUBE_ID}
        title="See how JOBUWIN works"
      />
    </div>
  );
}

export default old1_HomePage;
